import { Attr, BelongsTo, HasMany, HasOne, Model, Link } from 'spraypaint';
import moment from 'moment-timezone';

import { ApplicationRecord } from './application_record';
import AvailabilityZone from './availability_zone';
import Facility from './facility';
import Group from './group';
import Image from './image';
import InstanceType from './instance_type';
import Template from './template';
import WorkstationConnection from './workstation_connection';
import WorkstationCreation from './workstation_creation';
import WorkstationCreationGroup from './workstation_creation_group';
import WorkstationDeletion from './workstation_deletion';
import WorkstationEventFeedEntry from './workstation_event_feed_entry';
import WorkstationGroupAssignment from './workstation_group_assignment';
import WorkstationLogin from './workstation_login';
import WorkstationBoot from './workstation_boot';
import User from './user';
// import WorkstationStat from './workstation_stat';
import Stat from './stat';
import { isBootPending } from '../shared/boot_feedback';

@Model()
export default class Workstation extends ApplicationRecord {
  static jsonapiType = 'workstations';
  @Attr() name: string;
  @Attr() ipAddress: string;
  @Attr() managementIpAddress: string;
  @Attr() managementProtocol: string;
  @Attr() managementEndpoint: string;
  @Attr() instanceId: string;
  @Attr() stackId: string;
  @Attr() stackName: string;
  @Attr() isFailed: string;
  @Attr({ persist: false }) selected: boolean;
  @Attr() pending: boolean;
  @Attr() multiAz: boolean;
  @Attr() ebsRaid: boolean;
  @Attr() instanceState: string;
  @Attr() createdAt: string;
  @Attr() updatedAt: string;
  @Attr() discardedAt: string;
  @Attr() availabilityZoneIdentifier: string;
  @Attr() ssmConnectionCommand: string;
  @Attr() pcoipActive: boolean;
  @Attr() pcoipVersion: string;
  @Attr() pcoipAgentUpdatePending: boolean;
  @Attr() pendingBoot: boolean;
  @Attr() pendingRebuild: boolean;
  @Attr() pendingShutdown: boolean;
  @Attr() pendingReboot: boolean;
  @Attr() pendingHardReboot: boolean;
  @Attr() enableInstanceProtection: boolean;
  @Attr() latest: boolean;
  @Attr() locked: boolean;
  @Attr() platform: string;
  @Attr() rebuiltAt: string;
  @Attr() deploymentType: string;
  @Attr() placementGroup: string;

  @Attr() render: boolean;
  @Attr() infrastructure: boolean;
  @Attr() enableParsec: boolean;
  @Attr() enableNiceDcv: boolean;
  @Attr() parsecPeerId: string;

  @Attr() maneAgentBuildVersion: string;
  @Attr() maneAgentUpdatePending: boolean;
  @Attr() idleShutdownTogglePending: boolean;
  @Attr() idleShutdownEnabled: boolean;

  @Attr() lastLoginAt: string;
  @Attr() lastLoggedOutAt: string;
  @Attr() lastLoginActive: boolean;
  @Attr() lastConnectionActive: boolean;
  @Attr() lastLoginActiveAndConnected: boolean;
  @Attr() lastLoginUsername: string;
  @Attr() lastLoginName: string;
  @Attr() lastLoginInitials: string;
  @Attr() lastLoginAvatarUrl: string;
  @Attr() lastConnectedAt: string;
  @Attr() lastDisconnectedAt: string;

  @Attr() totalHours: number;
  @Attr() recentHours: number;
  @Attr() mtdHours: number;

  @BelongsTo() creationGroup: WorkstationCreationGroup;
  // The pushed document sideloads these, so they name their class: a
  // type-only import is dropped and the type would never be registered.
  @BelongsTo(InstanceType) instanceType: InstanceType;
  @BelongsTo(InstanceType) actualInstanceType: InstanceType;
  @BelongsTo(Image) image: Image;
  @BelongsTo(Template) template: Template;
  @BelongsTo() facility: Facility;
  @BelongsTo() availabilityZone: AvailabilityZone;
  @HasOne() creation: WorkstationCreation;
  @HasOne() deletion: WorkstationDeletion;
  @HasOne() pendingDeletion: WorkstationDeletion;
  @HasOne() lastLogin: WorkstationLogin;
  @HasOne() lastRebuild: WorkstationBoot;
  // The newest boot, whatever its state.
  @HasOne(WorkstationBoot) lastBoot: WorkstationBoot;
  @HasOne(WorkstationConnection) lastConnection: WorkstationConnection;
  @HasMany() events: WorkstationEventFeedEntry;
  @HasMany() workstationGroupAssignments: WorkstationGroupAssignment[];
  @HasMany() groups: Group[];
  @HasOne() cpuStat: Stat;
  @HasOne() workstationHoursStat: Stat;

  @Link() delete: string;
  @Link() boot: string;
  @Link() shutdown: string;
  @Link() reboot: string;
  @Link() rebuild: string;
  @Link() patch: string;

  constructor(attrs?: Record<string, any>) {
    const defaults = { selected: false };
    super({ ...defaults, ...attrs });
  }

  // The instance type to surface in the UI: the actual provisioned type when
  // present, otherwise the requested one. They diverge when capacity forces a
  // temporary substitution.
  displayInstanceType(): InstanceType {
    return this.actualInstanceType || this.instanceType;
  }

  // True when the provisioned type differs from the requested type — used to
  // flag a temporary change in the UI.
  instanceTypeChanged(): boolean {
    return Boolean(
      this.actualInstanceType &&
        this.instanceType &&
        this.actualInstanceType.id !== this.instanceType.id,
    );
  }

  isOnPrem() {
    return this.deploymentType === 'on_prem';
  }

  isStopped() {
    return this.instanceState === 'stopped';
  }

  isRunning() {
    return this.instanceState === 'running';
  }

  isSleeping() {
    return this.instanceState === 'sleeping';
  }

  showRebootInProgress() {
    return this.isOnPrem() && Boolean(this.pendingReboot);
  }

  // Waiting for its instance to be recreated: the old one is going or gone
  // while a boot is pending (a capacity fail-over or a rebuild), or the boot
  // has handed the instance to the auto scaling group. Shown instead of
  // "terminated", which is alarming for a machine that is on its way back.
  isReprovisioning() {
    if (this.isRunning()) {
      return false;
    }
    if (
      this.pendingBoot &&
      (this.instanceState === 'terminated' ||
        this.instanceState === 'shutting-down')
    ) {
      return true;
    }
    return isBootPending(this.lastBoot) && this.lastBoot.phase === 'recreating';
  }

  indicatorState() {
    if (this.showRebootInProgress()) {
      return 'rebooting';
    }
    return this.isReprovisioning() ? 'reprovisioning' : this.instanceState;
  }

  isPending() {
    return (
      this.instanceState === 'pending' || (this.pending && this.isStopped())
    );
  }

  isStopping() {
    return (
      this.instanceState === 'stopping' || (this.pending && this.isRunning())
    );
  }

  lastUserInfo() {
    if (this.lastLoginUsername === null) return '-';
    return `${this.lastLoginUsername} - ${moment
      .utc(this.lastLoginAt)
      .local()
      .calendar()}`;
  }

  hasGroups() {
    return this.groups.length > 0;
  }

  screens(): any[] {
    if (!(this.lastConnection && this.lastConnection.screens)) {
      return [];
    }

    return this.lastConnection.screens.map(screen => {
      const [x, y] = screen.split(' x ');
      return [parseInt(x), parseInt(y)];
    });
  }

  formattedCreatedAt() {
    return this.createdAt
      ? moment
          .utc(this.createdAt)
          .local()
          .format('MMM Do HH:mm')
      : '';
  }

  formattedRebuiltAt() {
    return this.rebuiltAt
      ? moment
          .utc(this.rebuiltAt)
          .local()
          .format('MMM Do HH:mm')
      : '';
  }

  // Whether someone other than the user signing in as `username` is
  // connected, which keeps everyone else from connecting; the connected user
  // can still reconnect. Mane ties a connection to the client user whose
  // username it carries. Unknown without the connection loaded.
  connectedByOther(username: string): boolean {
    const connection = this.lastConnection;
    return Boolean(connection?.active) && connection.username !== username;
  }

  disableConnect() {
    if (this.creation?.isPending() || this.showRebootInProgress()) {
      return true;
    }
    // Connecting during a boot opens the dialog onto its progress, so it can
    // be brought back after being closed. Mane offers no boot link while one
    // is pending, so the dialog won't start another.
    if (this.pendingBoot) {
      return false;
    }
    if (this.instanceState === 'pending' || this.instanceState === 'running') {
      return false;
    }
    if (this.isSleeping()) {
      return true;
    }
    return !this.links.boot;
  }

  disabledReason() {
    if (!this.disableConnect()) {
      return null;
    }
    if (this.creation?.isPending()) {
      return 'Workstation is being created';
    }
    if (this.isSleeping()) {
      return 'Workstation is currently sleeping';
    }
    if (this.showRebootInProgress()) {
      return 'Workstation is rebooting';
    }
    return null;
  }

  formattedLoggedInAt() {
    return moment
      .utc(this.lastLoginAt)
      .local()
      .format('MMM Do HH:mm');
  }

  formattedLoggedOutAt() {
    return moment
      .utc(this.lastLoggedOutAt)
      .local()
      .format('MMM Do HH:mm');
  }

  formattedConnectedAt() {
    return moment
      .utc(this.lastConnectedAt)
      .local()
      .format('MMM Do HH:mm');
  }

  formattedDisconnectedAt() {
    return moment
      .utc(this.lastDisconnectedAt)
      .local()
      .format('MMM Do HH:mm');
  }

  fakeAvatar() {
    return User.fakeAvatar(this.name);
  }
}
